import { toast } from "sonner";
/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import {useEffect, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {compareBy, Order, stableSort} from "../../helpers/TableHelpers";
import {FilterCombobox} from "../common/FilterCombobox";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import JobRequestStatusChip from "../Jobs/JobRequestStatusChip";
import {JobRequestStatus, JobRequestStatusColors, JobRequestStatusIcons, JobRequestStatusLabels,} from "../../models/Jobs/JobRequestStatus";
import {DeleteCaseRequest, JobRequest} from "../../models/Jobs/JobRequest";
import AbortJobModal from "../Jobs/AbortJobModal";
import JobsRepository from "../../repositories/JobsRepository";
import Fuse from "fuse.js";
import ConfirmMigrationEntitiesDeletionModal, {CONFIRM_MIGRATION_ENTITIES_DELETION_CONFIGS} from "../common/ConfirmMigrationEntitiesDeletionModal";
import { cn } from "@/lib/utils";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import {
  drawerFiltersBoxClassName,
  drawerFilterItemClassName,
  drawerSearchClassName,
  drawerTableWrapperClassName,
} from "../common/Table/TableStyles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, Trash2, XCircle } from "lucide-react";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { BulkDeletionSummary } from "../common/BulkDeletionSummary";

interface DeleteCasesTableProps {
  organizationId: string;
  newCasesForDeletion: DeleteCaseRequest[];
  setNewCasesForDeletion: React.Dispatch<React.SetStateAction<DeleteCaseRequest[]>>;
}

const DeleteCasesTable: React.FC<DeleteCasesTableProps> = ({
  organizationId,
  newCasesForDeletion,
  setNewCasesForDeletion,
}) => {
  const [order, setOrder] = React.useState<Order>("asc");
  const [sort, setSort] = React.useState<keyof JobRequest>("status");
  const [statusFilter, setStatusFilter] = React.useState<string | null>(null);
  const [searchForCaseNumber, setSearchForCaseNumber] = useState<string | null>("");
  const [abortJob, setAbortJob] = useState<JobRequest | null>(null);
  const [confirmDeletionModalOpen, setConfirmDeletionModalOpen] = useState<boolean>(false);
  const [casesDeletionStatus, setCasesDeletionStatus] = useState<DeleteCaseRequest[] | null>(null);
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();

  const casesQuery = useQuery({ queryKey: [QUERY_KEYS.CASES_DELETION_STATUS, organizationId], queryFn:
    async () => {
      return await jobsRepository.getCasesDeletionStatus(organizationId);
    }, staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000 + 1,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });

  useEffect(() => {
    if (casesQuery.data) {
      setCasesDeletionStatus(casesQuery.data);
    }
  }, [casesQuery]);

  const handleSearch = (newCaseNumber: string) => {
    setSearchForCaseNumber(newCaseNumber ?? null);
  };

  const changeSort = (newSort: keyof JobRequest) => {
    setSort(newSort);
  };

  const changeOrder = (newOrder: Order) => {
    setOrder(newOrder);
  };

  const { isError, isFetching } = casesQuery;

  const renderFilters = () => (
    <React.Fragment>
      <div className={drawerFilterItemClassName}>
        <Label className="text-sm">Status</Label>
        <FilterCombobox
          value={statusFilter}
          onChange={(newValue) => {
            setStatusFilter(newValue);
          }}
          placeholder="Status"
          options={Object.values(JobRequestStatus).map((status) => ({
            value: status as string,
            label: JobRequestStatusLabels[status as JobRequestStatus],
            color: JobRequestStatusColors[status as JobRequestStatus],
            icon: JobRequestStatusIcons[status as JobRequestStatus],
          }))}
        />
      </div>
    </React.Fragment>
  );

  const filteredJobs = (casesDeletionStatus || []).filter((job) => {
    return statusFilter == null || job.status === statusFilter;
  });

  const searchOptions = {
    keys: ["caseNumber"],
    includeScore: true,
    threshold: 0.1,
    caseSensitive: false,
  };

  const fuse = new Fuse(filteredJobs, searchOptions);
  const searchResults = searchForCaseNumber
    ? fuse.search(searchForCaseNumber).map((result) => result.item)
    : filteredJobs;
  const sortedJobs = sort
    ? stableSort<DeleteCaseRequest>(searchResults, compareBy(order, sort))
    : filteredJobs;
  const allRows = [...newCasesForDeletion, ...sortedJobs];
  const newIds = new Set(newCasesForDeletion.map((c) => c.migrationEntityId));

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={drawerFiltersBoxClassName}>
        <div className={drawerSearchClassName}>
          <Label className="text-sm">Search cases</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Case number"
              onChange={(event) => handleSearch(event.target.value)}
            />
          </div>
        </div>
        {renderFilters()}
      </div>
      <Button
        disabled={!(newCasesForDeletion.length > 0)}
        onClick={() => setConfirmDeletionModalOpen(true)}
      >
        Delete Cases
      </Button>
      <div
        className={cn(drawerTableWrapperClassName, (isFetching || isError) && "opacity-50 pointer-events-none")}
      >
        <Table containerClassName="flex-1 min-h-0">
            <TableHeader>
              <TableRow>
                <TableHead className="w-0 text-center" />

                <TableHead>
                  <SortableTableColumnHeader
                    label={"Case Number"}
                    sortProperty={"caseNumber"}
                    getSort={() => sort}
                    setSort={(newSort) =>
                      changeSort(newSort as keyof JobRequest)
                    }
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>

                <TableHead>
                  <SortableTableColumnHeader
                    label={"Status"}
                    sortProperty={"status"}
                    getSort={() => sort}
                    setSort={(newSort) =>
                      changeSort(newSort as keyof JobRequest)
                    }
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>

            <TableBody>
              {allRows.map((deleteCaseJob) => {
                const canEdit =
                  deleteCaseJob.status === JobRequestStatus.Pending ||
                  deleteCaseJob.status === JobRequestStatus.New ||
                  deleteCaseJob.status === JobRequestStatus.Completed;
                return (
                  <TableRow
                    key={deleteCaseJob.id ?? deleteCaseJob.migrationEntityId}
                    className={cn(newIds.has(deleteCaseJob.migrationEntityId) && "bg-primary/10")}
                  >
                    <TableCell className="text-center" />
                    <TableCell>
                      <span className="text-sm">
                        {deleteCaseJob.caseNumber}
                        {deleteCaseJob.bulk && <BulkDeletionSummary bulk={deleteCaseJob.bulk} />}
                      </span>
                    </TableCell>
                    <TableCell>
                      <JobRequestStatusChip status={deleteCaseJob.status} />
                    </TableCell>
                    {canEdit ? (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Cancel"
                          className="text-destructive"
                          onClick={() => {
                            deleteCaseJob.id
                              ? setAbortJob(deleteCaseJob)
                              : setNewCasesForDeletion((prevRows) =>
                                  prevRows.filter(
                                    (entity) =>
                                      entity.migrationEntityId !==
                                      deleteCaseJob.migrationEntityId
                                  )
                                );
                          }}
                        >
                          {deleteCaseJob.status ===
                          JobRequestStatus.Completed ? (
                            <Trash2 className="size-4" />
                          ) : (
                            <XCircle className="size-4" />
                          )}
                        </Button>
                      </TableCell>
                    ) : (
                      <TableCell />
                    )}
                  </TableRow>
                );
              })}
              {allRows.length === 0 && !isFetching && (
                <TableRow>
                  <TableCell colSpan={99} className="h-24 text-center text-muted-foreground">
                    No pending deletions.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
      </div>
      {abortJob ? (
        <AbortJobModal
          open={abortJob !== null}
          jobRequest={abortJob}
          onClose={() => {
            setAbortJob(null);
          }}
          onConfirm={async () => {
            await jobsRepository.deleteJob(abortJob.id);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            queryClient.invalidateQueries({ queryKey: [
              QUERY_KEYS.CASES_DELETION_STATUS,
              organizationId,
            ] });
            if (abortJob.status !== JobRequestStatus.Completed) {
              queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASES, organizationId] });
            }
            setAbortJob(null);
          }}
        />
      ) : (
        <></>
      )}

      {confirmDeletionModalOpen ? (
        <ConfirmMigrationEntitiesDeletionModal
          open={confirmDeletionModalOpen}
          entityKeys={newCasesForDeletion.map((c) => c.caseNumber)}
          confirmMigrationDeletionConfig={CONFIRM_MIGRATION_ENTITIES_DELETION_CONFIGS.CASES}
          onClose={() => {
            setConfirmDeletionModalOpen(false);
          }}
          onConfirm={async () => {
            await jobsRepository.startCasesDeletion(
              organizationId,
              newCasesForDeletion.map((c) => c.migrationEntityId)
            );
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            queryClient.invalidateQueries({ queryKey: [
              QUERY_KEYS.CASES_DELETION_STATUS,
              organizationId,
            ] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASES, organizationId] });
            setNewCasesForDeletion([]);
            setConfirmDeletionModalOpen(false);
            toast.success("Deletion started");
          }}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default DeleteCasesTable;
